import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import YogaClassCard from '../YogaClassCard';
import './Instructors.css';

// Custom Link component that scrolls to top
const ScrollToTopLink = ({ to, className, children }) => {
  const handleClick = () => {
    // Wait for next tick to ensure route change happens first
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };
  
  return (
    <Link to={to} className={className} onClick={handleClick}>
      {children}
    </Link>
  );
};

const InstructorProfile = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [instructor, setInstructor] = useState(null);
  const [instructorClasses, setInstructorClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [error, setError] = useState(null);
  const [classesError, setClassesError] = useState(null);
  const [adjacentInstructors, setAdjacentInstructors] = useState(null);
  const [selectedGalleryImage, setSelectedGalleryImage] = useState(null);
  const [selectedGalleryIndex, setSelectedGalleryIndex] = useState(0);
  const [parsedGalleryImages, setParsedGalleryImages] = useState([]);
  const [classImages, setClassImages] = useState({});
  const [touchStartX, setTouchStartX] = useState(null);
  
  // Define navigation functions
  const goToNextInstructor = () => {
    if (adjacentInstructors && adjacentInstructors.next_id) {
      navigate(`/instructors/${adjacentInstructors.next_id}`);
    }
  };

  const goToPrevInstructor = () => {
    if (adjacentInstructors && adjacentInstructors.previous_id) {
      navigate(`/instructors/${adjacentInstructors.previous_id}`);
    }
  };
  
  // Fetch class images for a specific class
  const fetchClassImages = async (classId) => {
    if (classImages[classId]) return; // Already fetched
    
    try {
      // Determine if we're on production or local
      const isProd = window.location.hostname === 'www.tideasolis.com' || window.location.hostname === 'tideasolis.com';
      
      // Create API URL based on environment
      let apiUrl = `/api/classes/${classId}/images`;
      if (isProd) {
        // Use absolute URL on production
        apiUrl = `https://${window.location.hostname}/api/classes/${classId}/images`;
      }
      
      const response = await axios.get(apiUrl, { 
        headers: { 'Accept': 'application/json' }
      });
      
      if (response.data && response.data.images && Array.isArray(response.data.images) && response.data.images.length > 0) {
        setClassImages(prev => ({
          ...prev,
          [classId]: response.data.images
        }));
      } else {
        // Try to find the class to use its processed images as a fallback
        const classData = instructorClasses.find(c => c.id === classId);
        if (classData && classData.image_url) {
          // Create an array with image_url as the first item
          const imageArray = Array.isArray(classData.image_url) 
            ? classData.image_url 
            : [classData.image_url];
            
          setClassImages(prev => ({
            ...prev,
            [classId]: imageArray
          }));
        }
      }
    } catch (err) {
      // Try to use image_url as fallback on error
      const classData = instructorClasses.find(c => c.id === classId);
      if (classData && classData.image_url) {
        // Create an array with image_url as the first item
        const imageArray = Array.isArray(classData.image_url) 
          ? classData.image_url 
          : [classData.image_url];
          
        setClassImages(prev => ({
          ...prev,
          [classId]: imageArray
        }));
      }
    }
  };
  
  // Memoize the fetchClassImages function to avoid infinite loops
  const memoizedFetchClassImages = useCallback(
    (classId) => fetchClassImages(classId), 
    [classImages, instructorClasses]
  );
  
  // Fetch images for each class when instructorClasses changes
  useEffect(() => {
    if (!instructorClasses.length) return;
    
    instructorClasses.forEach(cls => {
      if (cls.id) {
        memoizedFetchClassImages(cls.id);
      }
    });
  }, [instructorClasses, memoizedFetchClassImages]);
  
  // Fetch adjacent instructor IDs
  useEffect(() => {
    const fetchAdjacentInstructors = async () => {
      try {
        // Determine if we're on production or local
        const isProd = window.location.hostname === 'www.tideasolis.com' || window.location.hostname === 'tideasolis.com';
        
        // Create API URL based on environment
        let apiUrl = `/api/instructors/${id}/adjacent`;
        if (isProd) {
          // Use absolute URL on production
          apiUrl = `https://${window.location.hostname}/api/instructors/${id}/adjacent`;
        }
        
        const response = await axios.get(apiUrl, { 
          headers: { 'Accept': 'application/json' }
        });
        
        setAdjacentInstructors(response.data);
      } catch (err) {
        // Silently fail for adjacent instructors
      }
    };
    
    fetchAdjacentInstructors();
  }, [id]);
  
  // Fetch instructor data
  useEffect(() => {
    const fetchInstructor = async () => {
      try {
        setLoading(true);
        
        // Determine if we're on production or local
        const isProd = window.location.hostname === 'www.tideasolis.com' || window.location.hostname === 'tideasolis.com';
        
        // Create API URL based on environment
        let apiUrl = `/api/instructors/${id}`;
        if (isProd) {
          // Use absolute URL on production
          apiUrl = `https://${window.location.hostname}/api/instructors/${id}`;
        }
        
        // Direct axios call with explicit URL
        const response = await axios.get(apiUrl, { 
          headers: { 'Accept': 'application/json' }
        });
        
        const data = response.data;
        
        setInstructor(data);
        
        // Parse the support_images if it's a string
        if (data && data.support_images) {
          let galleryImages = data.support_images;
          
          // Check if it's a PostgreSQL array string format like "{url1,url2,url3}"
          if (typeof galleryImages === 'string' && galleryImages.startsWith('{') && galleryImages.endsWith('}')) {
            // Remove the curly braces and split by comma
            galleryImages = galleryImages.substring(1, galleryImages.length - 1).split(',');
          }
          
          // Ensure it's an array
          if (!Array.isArray(galleryImages)) {
            galleryImages = [];
          }
          
          setParsedGalleryImages(galleryImages);
        } else {
          setParsedGalleryImages([]);
        }

        // Update document title and meta tags after fetching instructor data
        if (data) {
          // Determine if we're in Slovak language context
          const isSlovak = document.documentElement.lang === 'sk' || window.location.pathname.includes('/sk/');
          
          // Create Slovak variants of strings
          const instructorTitle = isSlovak ? (data.title_sk || data.title || t('yoga_instructor')) : (data.title || t('yoga_instructor'));
          const yogaStudioName = isSlovak ? 'Tidea Solis Yoga Studio' : 'Tidea Solis Yoga Studio';
          
          // Create title tags with Slovak focus
          const titleTag = `${data.name} - ${instructorTitle} | ${yogaStudioName}`;
          document.title = titleTag;
          
          // Create description with focus on Slovak
          let metaDescription = '';
          if (isSlovak) {
            metaDescription = `${data.name} - ${data.specialty || instructorTitle} v Rozhodove. ${data.bio_sk || data.bio ? (data.bio_sk || data.bio).substring(0, 120) + '...' : 'Intruktor jogy v Tidea Solis Yoga Studio.'}`;
          } else {
            metaDescription = `${data.name} - ${data.specialty || instructorTitle}. ${data.bio ? data.bio.substring(0, 150) + '...' : t('instructor_bio_placeholder')}`;
          }
          
          // Add/update meta tags with Slovak focus
          updateMetaTag('description', metaDescription);
          updateMetaTag('keywords', `joga, yoga, jga, yoga intruktor, jga intruktor, jgov cvienia, ${data.name}, ${data.specialty || ''}, ${instructorTitle}, Tidea Solis, Rozhodova, Slovensko, Slovakia, cvienia jgy`);
          
          // Open Graph tags with Slovak focus
          updateMetaTag('og:title', titleTag);
          updateMetaTag('og:description', metaDescription);
          updateMetaTag('og:image', getImageUrl(data.image_url));
          updateMetaTag('og:type', 'profile');
          updateMetaTag('og:url', window.location.href);
          updateMetaTag('og:site_name', yogaStudioName);
          
          // Language tags for Slovak
          updateMetaTag('og:locale', 'sk_SK');
          updateMetaTag('language', 'Slovak');
          
          // Create hreflang links for language alternates
          const createOrUpdateLink = (rel, hreflang, href) => {
            let link = document.querySelector(`link[rel="${rel}"][hreflang="${hreflang}"]`);
            if (!link) {
              link = document.createElement('link');
              link.rel = rel;
              link.hreflang = hreflang;
              document.head.appendChild(link);
            }
            link.href = href;
          };
          
          // Base URL without language path
          const baseUrl = window.location.href.replace(/\/(sk|en|hu)\//, '/');
          
          // Set up language alternates
          createOrUpdateLink('alternate', 'sk', baseUrl.replace(/^(https?:\/\/[^\/]+)/, '$1/sk'));
          createOrUpdateLink('alternate', 'en', baseUrl.replace(/^(https?:\/\/[^\/]+)/, '$1/en'));
          createOrUpdateLink('alternate', 'x-default', baseUrl.replace(/^(https?:\/\/[^\/]+)/, '$1/en'));
          
          // Create structured data for Slovak users
          const structuredData = {
            "@context": "https://schema.org",
            "@type": "Person",
            "name": data.name,
            "jobTitle": instructorTitle,
            "description": metaDescription,
            "image": getImageUrl(data.image_url),
            "url": window.location.href,
            "worksFor": {
              "@type": "Organization",
              "name": yogaStudioName,
              "url": "https://tideasolis.com"
            },
            "knowsLanguage": ["sk", "en"],
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Rozhodova",
              "addressCountry": "SK"
            }
          };
          
          // Add structured data to page
          let scriptTag = document.querySelector('script[type="application/ld+json"]');
          if (!scriptTag) {
            scriptTag = document.createElement('script');
            scriptTag.type = 'application/ld+json';
            document.head.appendChild(scriptTag);
          }
          scriptTag.textContent = JSON.stringify(structuredData);
          
          // Add canonical link
          const canonicalLink = document.querySelector('link[rel="canonical"]');
          if (!canonicalLink) {
            const link = document.createElement('link');
            link.rel = 'canonical';
            link.href = window.location.href;
            document.head.appendChild(link);
          } else {
            canonicalLink.href = window.location.href;
          }
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching instructor:', err);
        const status = err?.response?.status;
        if (status === 410 || status === 404) {
          // Instructor not found or deleted — show the not-found state
          setInstructor(null);
          setError(null);
        } else {
          setError(t('instructor_load_error'));
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchInstructor();
    
    // Cleanup function to remove added meta tags
    return () => {
      // Remove specific meta tags when component unmounts
      removeMetaTag('og:title');
      removeMetaTag('og:description');
      removeMetaTag('og:image');
      removeMetaTag('og:type');
      removeMetaTag('og:url');
      removeMetaTag('og:locale');
      removeMetaTag('og:site_name');
      
      // Remove structured data
      const scriptTag = document.querySelector('script[type="application/ld+json"]');
      if (scriptTag) {
        scriptTag.remove();
      }
    };
  }, [id, t]);
  
  // Fetch instructor's classes
  useEffect(() => {
    const fetchInstructorClasses = async () => {
      try {
        setLoadingClasses(true);
        
        // Determine if we're on production or local
        const isProd = window.location.hostname === 'www.tideasolis.com' || window.location.hostname === 'tideasolis.com';
        
        // Create API URL based on environment
        let apiUrl = `/api/instructors/${id}/classes`;
        if (isProd) {
          // Use absolute URL on production
          apiUrl = `https://${window.location.hostname}/api/instructors/${id}/classes`;
        }
        
        // Direct axios call with explicit URL
        const response = await axios.get(apiUrl, { 
          headers: { 'Accept': 'application/json' }
        });
        
        const data = response.data;
        
        // Filter for upcoming week only
        const today = new Date();
        const nextWeek = new Date(today);
        nextWeek.setDate(today.getDate() + 7);
        
        const upcomingWeekClasses = (data || []).filter(cls => {
          if (!cls.start_time) return false;
          const classDate = new Date(cls.start_time);
          return classDate >= today && classDate <= nextWeek;
        });
        
        // Process class data with field mapping awareness
        const processedClasses = upcomingWeekClasses.map(cls => {
          // Extract the class name from the appropriate field
          const className = cls.class_name || cls.name || cls.title || t('unnamed_class');
          
          // Process image URL - Handle different formats
          let processedImages = [];
          if (cls.image_url) {
            try {
              // First check if it's already a parsed array
              if (Array.isArray(cls.image_url) && cls.image_url.length > 0) {
                processedImages = cls.image_url;
              } 
              // Otherwise try to parse it as a JSON string
              else if (typeof cls.image_url === 'string') {
                // Handle both JSON array strings and direct URL strings
                if (cls.image_url.trim().startsWith('[')) {
                  const parsed = JSON.parse(cls.image_url);
                  if (Array.isArray(parsed) && parsed.length > 0) {
                    processedImages = parsed;
                  }
                } else if (cls.image_url.includes('http')) {
                  // It's a direct URL, not a JSON string
                  processedImages = [cls.image_url];
                }
              }
            } catch (e) {
              // Fallback - if parsing fails but it looks like a URL, use it directly
              if (typeof cls.image_url === 'string' && cls.image_url.includes('http')) {
                processedImages = [cls.image_url];
              }
            }
          }
          
          return {
            ...cls,
            title: className,
            processedImages: processedImages
          };
        });
        
        setInstructorClasses(processedClasses);
        setClassesError(null);
      } catch (err) {
        console.error('Error fetching instructor classes:', err);
        setClassesError(t('no_classes_message'));
      } finally {
        setLoadingClasses(false);
      }
    };
    
    fetchInstructorClasses();
  }, [id, t]);
  
  // Utility function to update meta tags
  const updateMetaTag = (name, content) => {
    // First check if the meta tag exists
    let metaTag = document.querySelector(`meta[name="${name}"], meta[property="${name}"]`);
    
    if (!metaTag) {
      // Create new meta tag if it doesn't exist
      metaTag = document.createElement('meta');
      // Determine if it's a standard meta or og: meta
      if (name.startsWith('og:')) {
        metaTag.setAttribute('property', name);
      } else {
        metaTag.setAttribute('name', name);
      }
      document.head.appendChild(metaTag);
    }
    
    // Set or update the content
    metaTag.setAttribute('content', content);
  };
  
  // Utility function to remove meta tags
  const removeMetaTag = (name) => {
    const metaTag = document.querySelector(`meta[name="${name}"], meta[property="${name}"]`);
    if (metaTag) {
      metaTag.remove();
    }
  };
  
  // Handle class booking
  const handleBookClass = (yogaClass) => {
    // This would open a booking modal or redirect to booking page
    // Implement booking functionality here
    alert(`Booking class: ${yogaClass.title} with ${yogaClass.instructor_name}`);
  };
  
  // Handle image URLs - support both absolute and relative paths
  const getImageUrl = (image_url) => {
    return image_url || `${process.env.PUBLIC_URL}/images/default-instructor.jpg`;
  };
  
  // Open gallery image in full-screen modal
  const openGalleryImage = (imageUrl, index) => {
    // Lock scrolling on the body when gallery opens
    document.body.style.overflow = 'hidden';
    setSelectedGalleryImage(imageUrl);
    setSelectedGalleryIndex(index);
  };
  
  // Close gallery modal
  const closeGalleryModal = () => {
    // Restore scrolling when gallery closes
    document.body.style.overflow = '';
    setSelectedGalleryImage(null);
  };

  // Navigate to previous image in gallery
  const prevGalleryImage = (e) => {
    if (e) e.stopPropagation();
    const newIndex = (selectedGalleryIndex - 1 + parsedGalleryImages.length) % parsedGalleryImages.length;
    setSelectedGalleryIndex(newIndex);
    setSelectedGalleryImage(parsedGalleryImages[newIndex]);
  };

  // Navigate to next image in gallery
  const nextGalleryImage = (e) => {
    if (e) e.stopPropagation();
    const newIndex = (selectedGalleryIndex + 1) % parsedGalleryImages.length;
    setSelectedGalleryIndex(newIndex);
    setSelectedGalleryImage(parsedGalleryImages[newIndex]);
  };

  // Handle touch start for swipe
  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
  };

  // Handle touch end for swipe
  const handleTouchEnd = (e) => {
    if (touchStartX === null) return;
    
    const touchEndX = e.changedTouches[0].clientX;
    const diffX = touchEndX - touchStartX;
    
    // If swipe distance is significant (more than 50px)
    if (Math.abs(diffX) > 50) {
      if (diffX > 0) {
        // Swipe right -> go to previous image
        prevGalleryImage();
      } else {
        // Swipe left -> go to next image
        nextGalleryImage();
      }
    }
    
    setTouchStartX(null);
  };

  // Get difficulty text based on numerical value
  const getDifficultyText = (difficulty) => {
    if (!difficulty) return t('normal');
    
    // Convert to number if it's a string
    const difficultyNum = parseInt(difficulty, 10);
    
    switch(difficultyNum) {
      case 1:
        return t('relax');
      case 2:
        return t('medium');
      case 3:
        return t('dynamic');
      default:
        return t('normal');
    }
  };

  // Get difficulty class
  const getDifficultyClass = (difficulty) => {
    if (!difficulty) return 'difficulty-unknown';
    
    // Convert to number if it's a string
    const difficultyNum = parseInt(difficulty, 10);
    
    switch(difficultyNum) {
      case 1:
        return 'difficulty-beginner';
      case 2:
        return 'difficulty-intermediate';
      case 3:
        return 'difficulty-advanced';
      default:
        return 'difficulty-unknown';
    }
  };
  
  if (loading) {
    return (
      <div className="instructor-profile-page">
        <div className="container">
          <div className="instructor-profile-loading">
            <div className="spinner"></div>
            <p>{t('loading_instructor_profile')}</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="instructor-profile-page">
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="container">
          <div className="instructor-profile-error">
            <p>{error}</p>
            <Link to="/" className="back-link">{t('back_to_home')}</Link>
          </div>
        </div>
      </div>
    );
  }
  
  if (!instructor) {
    return (
      <div className="instructor-profile-page">
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="container">
          <div className="instructor-profile-not-found">
            <h2>{t('instructor_not_found')}</h2>
            <p>{t('instructor_not_exist')}</p>
            <Link to="/" className="back-link">{t('back_to_home')}</Link>
          </div>
        </div>
      </div>
    );
  }
  
  // Check if there are gallery images
  const hasGallery = parsedGalleryImages.length > 0;
  
  return (
    <div className="instructor-profile-page">
      <div className="container">
        {/* Profile navigation arrows */}
        <div className="profile-navigation">
          <button 
            className="nav-arrow nav-arrow-prev" 
            onClick={goToPrevInstructor}
            aria-label={t('previous_instructor')}
            disabled={!adjacentInstructors?.previous_id}
          >
            <span>&#8249;</span>
          </button>
          
          <div className="instructor-position">
            {adjacentInstructors && (
              <span>
                {t('instructor')} {adjacentInstructors.current_position} / {adjacentInstructors.total_instructors}
              </span>
            )}
          </div>
          
          <button 
            className="nav-arrow nav-arrow-next" 
            onClick={goToNextInstructor}
            aria-label={t('next_instructor')}
            disabled={!adjacentInstructors?.next_id}
          >
            <span>&#8250;</span>
          </button>
        </div>
        
        <article className="instructor-profile-content">
          <div className="instructor-profile-image">
            <img 
              src={getImageUrl(instructor.image_url)} 
              alt={`${instructor.name} - ${instructor.title || t('yoga_instructor')}`}
              onError={(e) => { 
                e.target.src = `${process.env.PUBLIC_URL}/images/default-instructor.jpg`; 
              }}
            />
          </div>
          
          <div className="instructor-profile-details">
            <h1 className="instructor-profile-name">{instructor.name}</h1>
            <p className="instructor-profile-title">{instructor.title}</p>
            
            {instructor.specialty && (
              <div className="instructor-profile-specialty">
                <span>{instructor.specialty}</span>
              </div>
            )}
            
            <div className="instructor-profile-bio">
              <h2>{t('biography')}</h2>
              <p className="formatted-bio" style={{ whiteSpace: 'pre-wrap' }}>
                {instructor.bio || t('no_biography')}
              </p>
            </div>
          </div>
        </article>
        
        {/* Instructor's Gallery Section */}
        {hasGallery && (
          <section className="instructor-gallery-section">
            <h2 className="section-title">{t('photo_gallery')}</h2>
            <div className="instructor-gallery">
              {parsedGalleryImages.map((imageUrl, index) => (
                <div 
                  key={index} 
                  className="gallery-item" 
                  onClick={() => openGalleryImage(imageUrl, index)}
                >
                  <img 
                    src={imageUrl} 
                    alt={`${instructor.name} - ${t('gallery_photo')} ${index + 1}`} 
                    className="gallery-image"
                    loading="lazy"
                    onError={(e) => { 
                      e.target.src = instructor.image_url || `${process.env.PUBLIC_URL}/images/default-instructor.jpg`; 
                    }}
                  />
                </div>
              ))}
            </div>
          </section>
        )}
        
        {/* Gallery Modal with Navigation */}
        {selectedGalleryImage && (
          <div className="gallery-modal" onClick={closeGalleryModal}>
            <div className="gallery-content" onClick={(e) => e.stopPropagation()}>
              <button className="gallery-close-btn" onClick={closeGalleryModal} aria-label={t('close_gallery')}>&times;</button>
              
              <button className="gallery-nav-btn gallery-prev-btn" onClick={prevGalleryImage} aria-label={t('previous_image')}>
                &#8249;
              </button>
              
              <img 
                src={selectedGalleryImage} 
                alt={`${instructor.name} - ${t('gallery_enlarged_view')}`}
                className="gallery-image"
                onError={(e) => { 
                  e.target.src = instructor.image_url || `${process.env.PUBLIC_URL}/images/default-instructor.jpg`; 
                }}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
              />
              
              <button className="gallery-nav-btn gallery-next-btn" onClick={nextGalleryImage} aria-label={t('next_image')}>
                &#8250;
              </button>
              
              <div className="gallery-counter">
                {selectedGalleryIndex + 1} / {parsedGalleryImages.length}
              </div>
            </div>
          </div>
        )}
        
        {/* Instructor's classes section */}
        <section className="instructor-classes-section">
          <h2 className="section-title">{t('upcoming_classes_with')} {instructor?.name}</h2>
          
          {loadingClasses ? (
            <div className="classes-loading">
              <div className="spinner"></div>
              <p>{t('loading_classes')}</p>
            </div>
          ) : classesError ? (
            <div className="classes-error">{classesError}</div>
          ) : instructorClasses.length > 0 ? (
            <div className="instructor-classes-container">
              {instructorClasses.map(yogaClass => {
                // Extract key information for display
                const classTitle = yogaClass.class_name || yogaClass.title || yogaClass.name || t('unnamed_class');
                const classDuration = yogaClass.duration || 60;
                const classCapacity = yogaClass.max_capacity || 20;
                const classPrice = yogaClass.price !== undefined ? yogaClass.price : 0;
                const classDifficulty = yogaClass.difficulty || 2;
                
                // Get the image URL from our fetched class images
                const imageUrl = classImages[yogaClass.id] && classImages[yogaClass.id][0] 
                  ? classImages[yogaClass.id][0] 
                  : yogaClass.processedImages && yogaClass.processedImages.length > 0
                    ? yogaClass.processedImages[0]
                    : `${process.env.PUBLIC_URL}/images/default-class.jpg`;
                
                return (
                  <article className="instructor-class-card" key={yogaClass.id}>
                    <div className="instructor-class-image">
                      <img 
                        src={imageUrl}
                        alt={classTitle}
                        loading="lazy"
                        onError={(e) => {
                          e.target.src = `${process.env.PUBLIC_URL}/images/default-class.jpg`;
                          // If default image also fails, use a data URI
                          e.target.onerror = () => {
                            e.target.parentNode.innerHTML = '<div class="no-image-text">Žiadne obrázky pre túto hodinu</div>';
                          };
                        }}
                      />
                    </div>
                    
                    <div className="instructor-class-content">
                      <h3 className="instructor-class-title">
                        {classTitle}
                        <span className={`instructor-difficulty-tag instructor-difficulty-${getDifficultyClass(classDifficulty)}`}>
                          {getDifficultyText(classDifficulty)}
                        </span>
                      </h3>
                      
                      <div className="instructor-class-info">
                        <div className="instructor-class-time-range">
                          <span className="instructor-time-icon">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </span>
                          <span className="instructor-time-values">
                            {(() => {
                              try {
                                // First check if formatted_time_range is available
                                if (yogaClass.formatted_time_range) {
                                  return yogaClass.formatted_time_range;
                                }
                                
                                // Use formatted_start_time and formatted_end_time if available
                                if (yogaClass.formatted_start_time && yogaClass.formatted_end_time) {
                                  return `${yogaClass.formatted_start_time} - ${yogaClass.formatted_end_time}`;
                                }
                                
                                // Fall back to manual ISO parsing if needed
                                if (!yogaClass.start_time && !yogaClass.date_time) return '';
                                
                                const dateTimeValue = yogaClass.start_time || yogaClass.date_time;
                                
                                // Parse as Date object
                                try {
                                  const date = new Date(dateTimeValue);
                                  if (!isNaN(date.getTime())) {
                                    // Format with localized time in 24h format
                                    const timeStr = date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', hour12: false});
                                    
                                    // If end_time is available, use it directly
                                    if (yogaClass.end_time) {
                                      const endDate = new Date(yogaClass.end_time);
                                      if (!isNaN(endDate.getTime())) {
                                        const endTimeStr = endDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', hour12: false});
                                        return `${timeStr} - ${endTimeStr}`;
                                      }
                                    }
                                    
                                    // Otherwise calculate from duration
                                    if (classDuration) {
                                      const endDate = new Date(date.getTime() + classDuration * 60000);
                                      return `${timeStr} - ${endDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', hour12: false})}`;
                                    }
                                    
                                    return timeStr;
                                  }
                                } catch (e) {
                                  // Silently fail on date parsing errors
                                }
                                
                                // Fallback to any available schedule information
                                return yogaClass.time || yogaClass.schedule_time || yogaClass.class_time || '';
                              } catch (e) {
                                // Silently fail
                                return '';
                              }
                            })()}
                          </span>
                        </div>
                        
                        <div className="instructor-class-date">
                          <span className="instructor-info-label">{t('date')}:</span>
                          <span className="instructor-date-value">
                            {(() => {
                              try {
                                // First check for explicit date field from backend
                                if (yogaClass.date) {
                                  return yogaClass.date;
                                }
                                
                                // Then try parsing start_time as a date
                                const dateTimeValue = yogaClass.start_time || yogaClass.date_time;
                                if (dateTimeValue) {
                                  const date = new Date(dateTimeValue);
                                  if (!isNaN(date.getTime())) {
                                    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
                                    return date.toLocaleDateString('sk-SK', options);
                                  }
                                }
                                
                                // Finally check for other date-related fields
                                return yogaClass.class_date || yogaClass.schedule_date || '';
                              } catch (e) {
                                console.error("Error formatting date:", e);
                                return '';
                              }
                            })()}
                          </span>
                        </div>

                        <div className="instructor-class-details-row">
                          <div className="instructor-class-duration">
                            <span className="instructor-info-label">{t('duration')}:</span> {classDuration} {t('minutes')}
                          </div>
                          <div className="instructor-class-capacity">
                            <span className="instructor-info-label">{t('capacity')}:</span> 
                            <span className="instructor-capacity-values">{classCapacity}</span>
                          </div>
                          <div className="instructor-class-price">
                            <span className="instructor-info-label">{t('price')}:</span> 
                            <span className="instructor-price-value">{classPrice}&nbsp;€</span>
                          </div>
                        </div>
                        
                        {yogaClass.description && (
                          <div className="instructor-class-description">
                            {yogaClass.description.length > 100 ? 
                              `${yogaClass.description.substring(0, 100)}...` : 
                              yogaClass.description}
                          </div>
                        )}
                      </div>
                      
                      <ScrollToTopLink to="/schedule" className="instructor-book-button">
                        {t('view_full_schedule')}
                      </ScrollToTopLink>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="no-classes-message">
              <p>{t('no_classes_message')}</p>
              <ScrollToTopLink to="/schedule" className="view-schedule-button">
                {t('view_full_schedule')}
              </ScrollToTopLink>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default InstructorProfile; 